import React, { useState, useEffect } from 'react';
import { getWhiteLabelConfig } from 'constants/WhiteLabelConfig';
import { getLocalStorageItem, addLocalStorageListener } from 'utils/localStorage';

/**
 * WhiteLabelWrapper Component
 * 
 * A wrapper component that conditionally renders its children based on white labeling configuration.
 * This component checks the white label configuration for the current customer and renders
 * its children only if navigation elements should be shown for that customer.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be rendered conditionally
 * @param {string} props.element - Not used anymore, kept for backwards compatibility
 * @returns {React.ReactNode|null} - Returns children if navigation should be shown, null otherwise
 */
const WhiteLabelWrapper = ({ children, element }) => {
  // State to store customer info to trigger re-render when it changes
  const [customerInfo, setCustomerInfo] = useState(() => {
    return getLocalStorageItem('customer_info');
  });
  
  // Get customer ID from state
  const customerId = customerInfo?.id;
  
  // Set up event listener for storage changes
  useEffect(() => {
    // Function to handle regular checking of localStorage
    const checkStorage = () => {
      const currentInfo = getLocalStorageItem('customer_info');
      
      // Only update state if the customer_id has changed
      if ((currentInfo?.id !== customerInfo?.id)) {
        setCustomerInfo(currentInfo);
      }
    };
    
    // Listen for localStorage changes (from our utility)
    const removeListener = addLocalStorageListener((change) => {
      if (change.key === 'customer_info') {
        const newInfo = change.newValue ? JSON.parse(change.newValue) : null;
        setCustomerInfo(newInfo);
      }
    });
    
    // Also set up interval as a backup for direct localStorage manipulations
    const intervalId = setInterval(checkStorage, 1000);
    
    // Cleanup function
    return () => {
      removeListener();
      clearInterval(intervalId);
    };
  }, [customerInfo?.id]);
  
  // Get white label configuration for this user
  const whiteLabelConfig = getWhiteLabelConfig(customerId);
  
  // Determine if navigation elements should be shown
  const shouldShow = whiteLabelConfig.showNavigation;
  
  return shouldShow ? children : null;
};

export default WhiteLabelWrapper;
